import { type FC, type ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@krea/shared/design-system/Button";

import { BANKID_TEST_IDS } from "../../test-constants";
import { SEBankIDAuthType } from "../types";

interface AlternativeAuthOptionsProps {
  currentAuthType: SEBankIDAuthType | null;
  onChangeAuthType: (authType: SEBankIDAuthType) => void;
  /**
   * The SSN test flow completes through the backend's dev backdoor, which
   * ADMIN_WEB refuses outright — an admin session always requires a real
   * identity. Hiding the option there beats offering a button that can only 403.
   */
  allowTestFlow?: boolean;
}

export const AlternativeAuthOptions: FC<AlternativeAuthOptionsProps> = ({
  currentAuthType,
  onChangeAuthType,
  allowTestFlow = true,
}) => {
  const { t } = useTranslation();
  const options = useMemo(() => {
    const availableOptions: ReactNode[] = [];

    // Show "Use on this device" when currently showing QR code
    if (currentAuthType === SEBankIDAuthType.QR_CODE) {
      availableOptions.push(
        <div key="auto-start" className="py-2">
          <Button
            size="lg"
            variant="secondary"
            className="w-full text-center"
            onClick={() => onChangeAuthType(SEBankIDAuthType.AUTO_START)}
          >
            {t("bankID.SE.bankIdOnThisDevice")}
          </Button>
        </div>,
      );
    }

    // Show "Use on another device" when currently showing auto-start
    if (currentAuthType === SEBankIDAuthType.AUTO_START) {
      availableOptions.push(
        <div key="qr-code" className="py-2">
          <Button
            size="lg"
            variant="secondary"
            className="w-full text-center"
            onClick={() => onChangeAuthType(SEBankIDAuthType.QR_CODE)}
          >
            {t("bankID.SE.bankIdOnAnotherDevice")}
          </Button>
        </div>,
      );
    }

    const showTestFlow = allowTestFlow && import.meta.env.MODE !== "production";

    // Show test flow option if enabled and not already in test flow
    if (
      showTestFlow &&
      currentAuthType !== SEBankIDAuthType.SSN &&
      currentAuthType !== null
    ) {
      availableOptions.push(
        <div key="test-flow" className="py-2">
          <Button
            size="lg"
            variant="secondary"
            className="w-full text-center"
            onClick={() => onChangeAuthType(SEBankIDAuthType.SSN)}
            data-test-id={BANKID_TEST_IDS.mobileBankIdTestFlowButton}
          >
            {t("bankID.SE.bankIdTestFlow")}
          </Button>
        </div>,
      );
    }

    return availableOptions;
  }, [currentAuthType, onChangeAuthType, allowTestFlow, t]);

  if (options.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col pt-6" style={{ maxWidth: 400 }}>
      {options}
    </div>
  );
};
